/**
 * buildMobileScheme.ts
 * ---------------------------------------------------------------------------
 * The Flutter app's theme generation, in TypeScript.
 *
 * Production path on mobile:
 *
 *   NAIAppTheme.colors.color_1
 *     -> AppBrandThemeV2.primary                (core/theme_v2/brand/app_brand_theme_v2.dart)
 *     -> ColorScheme.fromSeed(                  (core/theme_v2/builders/theme_v2_factory.dart)
 *          seedColor: primary,
 *          dynamicSchemeVariant: DynamicSchemeVariant.fidelity,
 *        )
 *
 * `SchemeFidelity` below is the SAME algorithm the Flutter SDK calls — both are
 * Google's material-color-utilities, one compiled into the Dart SDK and one
 * published to npm. Verified against the live catalogue at /mobile-theme: for
 * seed #456DB3 every role matches byte for byte. See buildMobileScheme.test.ts.
 *
 * Why fidelity matters: it keeps the tenant's configured colour as
 * `primaryContainer` and derives `primary` from it. The SDK does not pick a
 * different shade as the new seed, and neither do we.
 * ---------------------------------------------------------------------------
 */
import {
  Hct,
  MaterialDynamicColors,
  SchemeFidelity,
  argbFromHex,
  hexFromArgb,
} from '../../vendor/material-color-utilities'

/** Every role the Flutter catalogue renders, in its order. */
export const SCHEME_ROLES = [
  'primary',
  'onPrimary',
  'primaryContainer',
  'onPrimaryContainer',
  'secondary',
  'onSecondary',
  'secondaryContainer',
  'onSecondaryContainer',
  'tertiary',
  'onTertiary',
  'tertiaryContainer',
  'onTertiaryContainer',
  'error',
  'onError',
  'errorContainer',
  'onErrorContainer',
  'surface',
  'surfaceContainerLowest',
  'surfaceContainerLow',
  'surfaceContainer',
  'surfaceContainerHigh',
  'surfaceContainerHighest',
  'onSurface',
  'onSurfaceVariant',
  'outline',
  'outlineVariant',
  'inverseSurface',
  'inverseOnSurface',
  'inversePrimary',
  'scrim',
  'shadow',
] as const

export type SchemeRole = (typeof SCHEME_ROLES)[number]
export type MobileScheme = Record<SchemeRole, string>

/**
 * Tones lifted straight off the generated primary TonalPalette.
 *
 * Tailwind and MUI both want a light-to-dark 50..900 ramp; Material tones run
 * the other way, 100 (white) down to 0 (black). This is the one place where a
 * mapping had to be chosen rather than copied, so it is written down here and
 * nowhere else. Step 500 is not a tone lookup — it is the scheme's actual
 * `primary` role, so `palette.primary.main` equals what the phone renders.
 */
export const TONE_FOR_STEP: Record<number, number> = {
  50: 95,
  100: 90,
  200: 80,
  300: 70,
  400: 60,
  // 500 -> the scheme's primary role, not a tone
  600: 30,
  700: 25,
  800: 20,
  900: 10,
}

const dc = MaterialDynamicColors as unknown as Record<string, { getArgb(s: unknown): number } | undefined>

function schemeFor(seedHex: string, dark: boolean) {
  return new SchemeFidelity(Hct.fromInt(argbFromHex(seedHex)), dark, 0)
}

/** All 31 roles for one seed and one brightness. */
export function buildMobileScheme(seedHex: string, dark = false): MobileScheme {
  const scheme = schemeFor(seedHex, dark)
  const out = {} as MobileScheme
  for (const role of SCHEME_ROLES) {
    const color = dc[role]
    out[role] = color ? hexFromArgb(color.getArgb(scheme)).toLowerCase() : '#000000'
  }
  return out
}

/** The generated primary tonal palette, as a Tailwind-shaped 50..900 ramp. */
export function buildMobileTones(seedHex: string, dark = false): Record<number, string> {
  const scheme = schemeFor(seedHex, dark)
  const tones: Record<number, string> = {}
  for (const [step, tone] of Object.entries(TONE_FOR_STEP)) {
    tones[Number(step)] = hexFromArgb(scheme.primaryPalette.tone(tone)).toLowerCase()
  }
  const primary = dc.primary
  tones[500] = primary ? hexFromArgb(primary.getArgb(scheme)).toLowerCase() : '#000000'
  return tones
}

/**
 * The backend mixes formats in one payload: `primary` arrives as hex,
 * `color_1` as `rgba(69, 109, 179, 1)`, and the status map drops the spaces.
 * Anything that compares these as strings is already broken, so parse first.
 */
export function toHex(input: string | undefined | null): string | null {
  if (typeof input !== 'string') return null
  const value = input.trim()
  if (!value) return null

  const fn = value.match(/^rgba?\(\s*([^)]+)\)$/i)
  if (fn) {
    const parts = fn[1].replace(/\//g, ' ').split(/[\s,]+/).filter(Boolean)
    if (parts.length < 3) return null
    const channel = (raw: string) => {
      const n = raw.endsWith('%') ? (parseFloat(raw) / 100) * 255 : parseFloat(raw)
      return Math.max(0, Math.min(255, Math.round(n)))
    }
    const [r, g, b] = [channel(parts[0]), channel(parts[1]), channel(parts[2])]
    if ([r, g, b].some((n) => Number.isNaN(n))) return null
    return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`
  }

  const hex = value.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i)
  if (!hex) return null
  const h = hex[1].length === 3 ? hex[1].split('').map((c) => c + c).join('') : hex[1]
  return `#${h.toLowerCase()}`
}

/**
 * Pick the seed exactly as the phone does: `color_1` is the documented input.
 * `primary` is the fallback because ThemeAdd writes both from the same picker,
 * so they cannot disagree unless a row was edited outside that form.
 */
export function seedFromPayload(colors: Record<string, unknown> | null | undefined): string | null {
  const c = (colors ?? {}) as Record<string, string | undefined>
  return toHex(c.color_1) ?? toHex(c.primary)
}
